<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { get } from 'svelte/store';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { get_local_ip, real_id } from '../../../helpers/interface_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	//Services
	import { gen2Request } from '../../../services/gen_2_http_reqs';
	//Stores
	import { devices } from 'src/stores/lists/devices';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Design system components
	import { Text, Button, Alert, Toggle, Modal, Container } from '@Shelly/shelly-design-system';
	//Components
	import NetworkConnectedStatus from '../NetworkComponents/NetworkConnectedStatus.svelte';
	//Types
	import type { SettingRequest } from '../../../types/requests/settings/setting';
	import type { Device } from '../../../types/Device';
	import { capFirstChar } from '../../../helpers/utilities';
	import { requestEditDevice } from 'src/components/Lists/list-helpers';

	$: settingsStore = getDeviceSettings(id);
	export let device: Device;
	export let isOffline;

	const id = real_id(device?.id),
		realDevice = get(devices)?.[id],
		settings = $settingsStore,
		saveSetting = async () => {
			const res: SettingRequest = await gen2Request(device, 'Cloud.SetConfig', {
				config: { enable },
			});
			if (res.isok) {
				settings.cloud.enable = enable;
				devices_settings.update_devices_settings(id, settings);
				if (!enable) {
					requestEditDevice({ ...realDevice, cloud_online: false });
				}
			}
			onSettingsResponse(res, device);
			close();
		};

	let enable = settings?.cloud?.enable && realDevice?.cloud_online;

	let showModal = false;

	const toggleEnable = () => {
		enable = !enable;
	};

	const handleToggleClick = (e) => {
		enable = e.target.checked;
		showModal = true;
	};

	const close = () => {
		showModal = false;
	};

	const local_ip = get_local_ip(id);
</script>

<Toggle
	size="m"
	class="mb-s"
	checked={enable}
	on:click={handleToggleClick}
	disabled={isOffline}
	info={$showNewLayout
		? $format('cloud_description', {
				default:
					'Connecting your Shelly to the cloud allows you to control it remotely, receive notifications and updates about your devices.',
			})
		: undefined}
	id="toggle-jlucd11"
>
	{capFirstChar(
		$showNewLayout
			? $format('sub_settings_title_cloud', { default: 'cloud' })
			: $format('option_enable', { default: 'enable' }),
	)}
</Toggle>

<NetworkConnectedStatus connected={enable} />

{#if !$showNewLayout}
	<Alert
		heading={$format('cloud_description', {
			default:
				'Connecting your Shelly to the cloud allows you to control it remotely, receive notifications and updates about your devices.',
		})}
	/>
{/if}

<Modal
	on:close={() => {
		close();
		toggleEnable();
	}}
	{showModal}
	backdropClose={true}
	title={enable
		? $format('enable_cloud_connection_title', {
				default: 'Enable Cloud Connection?',
			})
		: $format('disable_cloud_connection_title', {
				default: 'Disable Cloud Connection?',
			})}
>
	<svelte:fragment slot="content">
		<Container padding="0">
			<Text>
				{#if enable}
					{$format('enable_cloud_modal_text', {
						default: `If you enable the device cloud support, you will be able to control and monitor your device remotely, outside its local network.`,
					})}
				{:else}
					{$format('disable_cloud_warning_modal', {
						default:
							'If you disable the device cloud support, you will lose connection to your device from outside its local network.',
					})}
				{/if}
			</Text>

			{#if !local_ip}
				<Alert
					class="mt-m"
					type="warning"
					heading={$format('alert_message_warning_non_local_cloud_disable', {
						default:
							'This device, is not found in your local network. If you disable cloud, you will lose connection to your device.',
					})}
				/>
			{/if}
		</Container>
	</svelte:fragment>
	<div class="d-flex justify-content-end" slot="footer">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			disabled={isOffline}
			variant="secondary"
			on:click={() => {
				close();
				toggleEnable();
			}}>{$format('button_cancel', { default: 'Cancel' })}</Button
		>
		<Button
			id="button_enable_disable"
			aria-labelledby="button_enable_disable"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			responsiveSize={{
				sm: 'm',
			}}
			disabled={isOffline || !realDevice}
			on:click={saveSetting}
		>
			{#if enable}
				{$format('button_enable', { default: 'Enable' })}
			{:else}
				{$format('disable', { default: 'Disable' })}
			{/if}
		</Button>
	</div>
</Modal>
